import { TitleButton } from '@/common/buttons/TitleButton';
import { useDialog } from '@/common/dialog/DialogContext';
import { EntityList } from '@/common/entityFramework/entityList/EntityList';
import { Mode } from '@/common/entityFramework/enumerations/Mode';
import { useNavState } from '@/common/navState/NavStateContext';
import { usePrincipal } from '@/common/principal/PrincipalContext';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { ExprUtils } from '@/common/utils/ExprUtils';
import { ObjUtils } from '@/common/utils/ObjUtils';
import { CalcOvrdDialog } from '@/gp/entities/calcProd/CalcOvrdDialog';
import { PolForumlaExprDialog } from '@/gp/entities/polFormula/PolFormulaExprDialog';
import { fas } from '@awesome.me/kit-b4fd369ea4/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconButton, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { useCallback, useRef, useState } from 'react';

/**
 * Per requirement GP-11, this provides a list of calculated values for a specific calculation product, as well as access to aggregate's transactions
 **/
export const CalcProdResList = ({ calcProdObj, calc }) => {
  const rsrc = useRsrc();
  const entityListRef = useRef();
  const { navigateTo } = useNavState();
  const { hasRole } = usePrincipal();
  const { openDialog } = useDialog();

  const [isSrcCalc, setIsSrcCalc] = useState(false);
  const [isOutline, setIsOutline] = useState(false);
  const [calcProdResRows, setCalcProdResRows] = useState([]);
  const linked11Ref = useRef('');

  const isRepro = calc.src !== 'PER';
  const typeOrder = ['FINAL','INTER','AGG','UNQAGG','NOMAGG','FACTOR','INFO']; 

  const augFieldDefs = useCallback((fieldToFieldDef) => {
    const columns = ['status', 'valStatus', 'name', 'code', 'type', 'lineCount', 'value', 'ovrdValue', 'ovrdComment', 'srcCalcValue'];
    fieldToFieldDef.status.maxWidth = 150;
    fieldToFieldDef.valStatus.maxWidth = 225;

    fieldToFieldDef.value.type = 'CALC_VALUE';
    
    fieldToFieldDef.lineCount = {
      field: 'lineCount',
      displayName: 'Line Count'
    }

    fieldToFieldDef.type = {
      field: 'type',
      sort: 'asc',
      sortIndex: 0,
      displayName: 'Type',
      comparator: (valueA, valueB) => {
        const indexA = typeOrder.indexOf(valueA);
        const indexB = typeOrder.indexOf(valueB);
        return indexA - indexB;
      },
    }

    fieldToFieldDef.srcCalcValue = {
      field: 'srcCalcValue',
      displayName: 'Source Calculation',
      initialHide: !isSrcCalc,
      type: 'CALC_VALUE',
    }

    if (isOutline) {
      fieldToFieldDef.code = {
        ...fieldToFieldDef.code,
        autoGrouping: true,
        rowGroup: true,
      }
    }

    fieldToFieldDef.ovrdValue.hide = isRepro;
    fieldToFieldDef.ovrdValue.type = 'CALC_VALUE';

    fieldToFieldDef.ovrdComment.hide = isRepro;

    Object.keys(fieldToFieldDef).forEach(colName => {
      if (!columns.includes(colName)) {
        delete fieldToFieldDef[colName];
      } else {
        const index = columns.indexOf(colName);
        fieldToFieldDef[colName].precedence = (index + 1) * 100
      }
    });
  });


  const openCalcOvrdDialog = () => {
    const data = {
      calc: calc,
      calcProd: calcProdObj,
      entityEntryPoint: 'CALC_PROD_RES',
      calcProdRows: calcProdResRows
    }
    openDialog(CalcOvrdDialog, { data })
  }

  const clientSideRowData = useCallback((setDataSource) => {
    if (isOutline) {
      const reqParam = {
        polId: calc.polId,
        polIds: [calc.polId],
        calcProdIds: [calcProdObj.id]
      }

      rsrc.data('gp', 'CALC_PROD_RES_DATA_PLUGIN', reqParam).then((res) => {
        addSrcVals(res);

        const linked11Value = res.find(row => row.code === 'LINKED11')?.value ?? '';
        linked11Ref.current = linked11Value;
        setDataSource(res);
      });
    } else {
      const reqParam = {
        partition: calc.partition,
        expr: ExprUtils.whereParams('calcProdId', calcProdObj.id)
      }

      rsrc.rows('gp', 'CALC_PROD_RES', reqParam).then((res) => {
        addSrcVals(res);

        const linked11Value = res.find(row => row.code === 'LINKED11')?.value ?? '';
        linked11Ref.current = linked11Value;
        setDataSource(res);
        setCalcProdResRows(res);
      });
    }

  }, [calcProdObj.id, isOutline]);

  const addSrcVals = (calcProdRess) => {
    for (const calcProdRes of calcProdRess) {
      if (calcProdRes.valStatus === 'NONE' & calc.calcSrc === 'PER') {
        // Not validated therefore no Val Results to handle
        continue;
      }

      const valRes = JSON.parse(calcProdRes.valRes);
      if (valRes != null) {
        const valResMap =
          valRes.MATCH_RES ||
          valRes.NO_MATCH_FINAL ||
          valRes.NO_MATCH_FINAL_MATCH_OVRD ||
          valRes.NO_MATCH_INTER;

        if (valResMap != null) {
          calcProdRes.srcCalcValue = valResMap[0]?.srcCalcValue;
        }
      }
    }
  }

  const augTabButtons = (buttons) => {
    ObjUtils.clearObj(buttons)

    buttons.push(
      <ToggleButtonGroup
        value={isOutline ? 'outline' : 'flat'}
        exclusive
        onChange={(e, value) => {
          setIsOutline(value === 'outline');
        }}
        sx={{ height: 40, color: 'primary.main' }}>
        <ToggleButton value="outline" data-test-id="toggle-outline" sx={{ whiteSpace: 'nowrap' }}>
          Outline
        </ToggleButton>
        <ToggleButton value="flat" data-test-id="toggle-flat" sx={{ whiteSpace: 'nowrap' }}>
          Flat
        </ToggleButton>
      </ToggleButtonGroup>
    )

    if (isRepro) {
      // Calc Src Button
      buttons.push(
        <IconButton
          color={isSrcCalc ? 'primary' : 'default'}
          onClick={(e) => {
            const gridApiRef = entityListRef.current.gridRef.current.gridApiRef;
            const newSrcCalcValue = !isSrcCalc;
            setIsSrcCalc(newSrcCalcValue);
            gridApiRef.current.setColumnsVisible(['srcCalcValue', 'srcOvrdValue'], newSrcCalcValue);
            gridApiRef.current.sizeColumnsToFit();
          }}>
          <FontAwesomeIcon icon={fas.faSearchDollar} />
        </IconButton>
      )
    } else if (calc.status === 'DRAFT') {
      // Override Calculations Button
      buttons.push(
        <TitleButton
          dataTestId={'calc-ovrd-button'}
          icon={fas.faPencil}
          toolTip="Override Values"
          onClick={openCalcOvrdDialog}
        />
      )
    }
  };

  const getContextMenuItems = (params) => {
    const data = params.node.data;

    let navState = {
      mode: Mode.DETAIL,
      entityId: params.node.data.id,
      partition: calc.partition
    }

    const contextMenuItems = [
      {
        name: 'Details',
        icon: fas.faInfoCircle,
        action: () => navigateTo('gp/calc-prod-res', navState)
      }
    ]
    const trxApplicable = (data.type === 'AGG' || data.type === 'UNQAGG' || data.type === 'NOMAGG');
    const hasCalcProdResView = hasRole('GP_CALC_PROD_RES_VIEW');

    if (trxApplicable && hasCalcProdResView) {
      const viewerReqParam = {
        code: 'AGG_CALC_TRX',
        data: data,
        calc: calc,
        ndc11: calcProdObj.code,
        ndc11ProdId: calcProdObj.ndc11ProdId ?? calcProdObj.ndc11Prod?.id,
      }
      contextMenuItems.push({
        name: 'Transactions',
        icon: fas.faSignInAlt,
        subMenu: [{
          name: 'NDC11',
          icon: fas.faSignInAlt,
          action: () => navigateTo('gp/agg-viewer', { viewerReqParam: { ...viewerReqParam, ndcLevel: 'NDC11' }, entity: 'CALC' })
        }, (    
        {
          name: 'NDC11 & Linked Products',
          icon: fas.faSignInAlt,
          disabled: !linked11Ref.current.includes(','),
          action: () => navigateTo('gp/agg-viewer', { viewerReqParam: { ...viewerReqParam, ndcLevel: 'NDC9' }, entity: 'CALC' })
        })]
      });
    }

    const valRes = JSON.parse(data.valRes);
    if (trxApplicable && (valRes?.CALC_TRX_WARN || valRes?.CALC_TRX_ERROR)) {
      const viewerReqParam = {
        code: 'CALC_TRX_VAL',
        data: data,
        calc: calc,
        ndc11: calcProdObj.code,
        ndcLevel: 'NDC9'
      }
      contextMenuItems.push({
        name: 'Transaction Validations',
        icon: fas.faSignInAlt,
        action: () => navigateTo('gp/val-viewer', { viewerReqParam, entity: 'CALC_TRX_VAL' })
      })
    }

    const formulaName = 'Formula Expression';
    const polFormulaApplicable = !['FACTOR', 'REF', 'INFO'].includes(data.type);
    const hasPolFormulaView = hasRole('GP_POL_FORMULA_VIEW');
    if (polFormulaApplicable && hasPolFormulaView) {
      contextMenuItems.push({
        name: formulaName,
        icon: fas.faInfoCircle,
        action: () => openPolFormulaExprDialog(data)
      });
    }

    return contextMenuItems;
  };

  const openPolFormulaExprDialog = (rowData) => {
    const data = {
      name: rowData.name,
      code: rowData.code,
      polId: calc.polId,
      module: 'gp',
    }
    openDialog(PolForumlaExprDialog, { data })
  }

  return (
    <EntityList
      key={isOutline}
      entity="CALC_PROD_RES"
      clientSideRowData={clientSideRowData}
      sortModel={[{ colId: 'type', sort: 'asc' }]}
      augFieldDefs={augFieldDefs}
      augTabButtons={augTabButtons}
      ref={entityListRef}
      gridOptions={{
        treeData: isOutline,
        defaultExpandLevel: 0,
        getContextMenuItems
      }}
    />
  )
}